/**
 * Login Page - Custom Auth UI
 *
 * Eigener Login-Flow mit Tailwind Design
 */

import { useState } from "react";
import { Auth } from "aws-amplify";
import { Mail, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import logo from "@/assets/logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { BRANDING } from "@tasty/shared";

interface LoginPageProps {
  onSignIn: () => void;
}

export default function LoginPage({ onSignIn }: LoginPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Forgot Password State
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetStep, setResetStep] = useState<"request" | "confirm">("request");
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const usernameOrEmail = email.trim();
      const user = await Auth.signIn(usernameOrEmail, password);

      // Falls Cognito einen Challenge-Flow verlangt, sind wir noch nicht "fertig" eingeloggt.
      // (z.B. NEW_PASSWORD_REQUIRED, MFA, etc.)
      if (user?.challengeName) {
        setError(
          "Dein Account benötigt einen zusätzlichen Login-Schritt (z.B. Passwort-Reset/MFA). Bitte nutze „Passwort vergessen?“ oder wende dich an den Support."
        );
        return;
      }

      // Verifizieren, dass die Session wirklich steht
      await Auth.currentAuthenticatedUser();
      onSignIn();
    } catch (err: any) {
      console.error("Sign in error:", err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const usernameOrEmail = email.trim();
      if (resetStep === "request") {
        await Auth.forgotPassword(usernameOrEmail);
        setResetStep("confirm");
      } else {
        await Auth.forgotPasswordSubmit(
          usernameOrEmail,
          resetCode,
          newPassword
        );
        setResetSuccess(true);
        setTimeout(() => {
          setForgotPasswordMode(false);
          setResetStep("request");
          setResetSuccess(false);
        }, 2000);
      }
    } catch (err: any) {
      console.error("Forgot password error:", err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (err: any): string => {
    switch (err.code) {
      case "UserNotFoundException":
        return "Kein Konto mit dieser E-Mail gefunden";
      case "NotAuthorizedException":
        return "E-Mail oder Passwort falsch";
      case "UserNotConfirmedException":
        return "Konto nicht bestätigt. Bitte E-Mail prüfen.";
      case "CodeMismatchException":
        return "Falscher Bestätigungscode";
      case "ExpiredCodeException":
        return "Code abgelaufen. Bitte neu anfordern.";
      case "LimitExceededException":
        return "Zu viele Versuche. Bitte später erneut versuchen.";
      default:
        return err.message || "Ein Fehler ist aufgetreten";
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center pb-2">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <img
              src={logo}
              alt={BRANDING.appName}
              className="w-16 h-16 rounded-2xl"
            />
          </div>
          <h1 className="text-2xl font-bold text-foreground">{BRANDING.appName}</h1>
          <p className="text-muted-foreground">Business Dashboard</p>
        </CardHeader>

        <CardContent className="pt-4">
          {!forgotPasswordMode ? (
            /* Login Form */
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="email">
                  E-Mail
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="ihre@email.de"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="password">
                  Passwort
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Anmelden...
                  </>
                ) : (
                  "Anmelden"
                )}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setForgotPasswordMode(true);
                  setError(null);
                }}
                className="w-full text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                Passwort vergessen?
              </button>
            </form>
          ) : (
            /* Forgot Password Form */
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="text-center mb-4">
                <h2 className="text-lg font-semibold">Passwort zurücksetzen</h2>
                <p className="text-sm text-muted-foreground">
                  {resetStep === "request"
                    ? "Gib deine E-Mail ein, um einen Code zu erhalten"
                    : "Gib den Code und dein neues Passwort ein"}
                </p>
              </div>

              {resetSuccess ? (
                <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg text-center">
                  <p className="text-green-700 dark:text-green-300">
                    ✅ Passwort erfolgreich geändert!
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <label
                      className="text-sm font-medium"
                      htmlFor="reset-email"
                    >
                      E-Mail
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="reset-email"
                        type="email"
                        placeholder="ihre@email.de"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        required
                        disabled={resetStep === "confirm"}
                      />
                    </div>
                  </div>

                  {resetStep === "confirm" && (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-medium" htmlFor="code">
                          Bestätigungscode
                        </label>
                        <Input
                          id="code"
                          type="text"
                          placeholder="123456"
                          value={resetCode}
                          onChange={(e) => setResetCode(e.target.value)}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label
                          className="text-sm font-medium"
                          htmlFor="new-password"
                        >
                          Neues Passwort
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="new-password"
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="pl-10 pr-10"
                            required
                            minLength={8}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Mindestens 8 Zeichen
                        </p>
                      </div>
                    </>
                  )}

                  {error && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <p className="text-sm text-destructive">{error}</p>
                    </div>
                  )}

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Bitte warten...
                      </>
                    ) : resetStep === "request" ? (
                      "Code senden"
                    ) : (
                      "Passwort ändern"
                    )}
                  </Button>
                </>
              )}

              <button
                type="button"
                onClick={() => {
                  setForgotPasswordMode(false);
                  setResetStep("request");
                  setError(null);
                }}
                className="w-full text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                ← Zurück zum Login
              </button>
            </form>
          )}

          {/* Footer */}
          <div className="mt-6 pt-4 border-t text-center">
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} {BRANDING.companyName}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
